import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Select, { GroupBase, MultiValue, OptionsOrGroups } from 'react-select'
import { PermissionAction, permissionsService, PermissionType } from '../../services/permissions.service'
import { userService } from '../../services/user.service'
import './InputPermissionsUser.scss'

export const WORKFLOW_DYNAMIC_RECIPIENT_SLUGS = ['owner', 'workflow_starter', 'document_creator'] as const
export type WorkflowDynamicRecipientSlug = (typeof WORKFLOW_DYNAMIC_RECIPIENT_SLUGS)[number]

const DYN_PREFIX = 'dyn:'

type AssigneeOption = { value: number | string; label: string; isDynamic?: boolean }

interface InputWorkflowStepAssigneesProps {
  userIds?: number[]
  dynamicRecipients?: string[]
  onChange?: (userIds: number[], dynamicRecipients: string[]) => void
  disabled?: boolean
  users?: Array<{ id: number; username: string }>
}

export const InputWorkflowStepAssignees: React.FC<InputWorkflowStepAssigneesProps> = ({
  userIds = [],
  dynamicRecipients = [],
  onChange,
  disabled = false,
  users: usersProp,
}) => {
  const { t } = useTranslation()
  const [usersState, setUsersState] = useState<Array<{ id: number; username: string }>>([])
  const [loading, setLoading] = useState(false)
  const users = usersProp ?? usersState

  useEffect(() => {
    if (usersProp != null) return
    const loadUsers = async () => {
      if (!permissionsService.currentUserCan(PermissionAction.View, PermissionType.User)) {
        setUsersState([])
        return
      }
      setLoading(true)
      try {
        const response = await userService.listAll()
        setUsersState(response.results || [])
      } catch {
        setUsersState([])
      } finally {
        setLoading(false)
      }
    }
    loadUsers().catch(() => {})
  }, [usersProp])

  const dynamicLabelForSlug = useCallback((slug: string): string => t(`document_workflow.dynamic_recipient_${slug}`), [t])

  const options: OptionsOrGroups<AssigneeOption, GroupBase<AssigneeOption>> = useMemo(() => {
    const dynamicOptions: AssigneeOption[] = WORKFLOW_DYNAMIC_RECIPIENT_SLUGS.map((slug) => ({
      value: `${DYN_PREFIX}${slug}`,
      label: dynamicLabelForSlug(slug),
      isDynamic: true,
    }))
    const userOptions: AssigneeOption[] = users.map((user) => ({
      value: user.id,
      label: user.username,
    }))
    const existingIds = new Set(userOptions.map((opt) => opt.value))
    userIds.forEach((id) => {
      if (!existingIds.has(id)) {
        userOptions.push({ value: id, label: `${t('user')} #${id}` })
      }
    })
    return [
      { label: t('document_workflow.dynamic_recipients_section'), options: dynamicOptions },
      { label: t('users'), options: userOptions },
    ]
  }, [users, userIds, t, dynamicLabelForSlug])

  const selectedOptions = useMemo(() => {
    const flatOptions: AssigneeOption[] = []
    options.forEach((group) => {
      if ('options' in group) {
        flatOptions.push(...group.options)
      }
    })
    const selectedValues = new Set([
      ...userIds,
      ...dynamicRecipients.map((slug) => `${DYN_PREFIX}${slug}`),
    ])
    const fromOptions = flatOptions.filter((opt) => selectedValues.has(opt.value))
    const covered = new Set(fromOptions.map((opt) => opt.value))
    dynamicRecipients.forEach((slug) => {
      const val = `${DYN_PREFIX}${slug}`
      if (!covered.has(val)) {
        fromOptions.push({ value: val, label: dynamicLabelForSlug(slug), isDynamic: true })
      }
    })
    userIds.forEach((id) => {
      if (!covered.has(id)) {
        fromOptions.push({ value: id, label: `${t('user')} #${id}` })
      }
    })
    return fromOptions
  }, [options, userIds, dynamicRecipients, t, dynamicLabelForSlug])

  const handleChange = (selected: MultiValue<AssigneeOption>) => {
    const nextUserIds: number[] = []
    const nextDynamic: string[] = []
    selected.forEach((opt) => {
      if (typeof opt.value === 'string' && opt.value.startsWith(DYN_PREFIX)) {
        nextDynamic.push(opt.value.slice(DYN_PREFIX.length))
      } else if (typeof opt.value === 'number') {
        nextUserIds.push(opt.value)
      }
    })
    onChange?.(nextUserIds, nextDynamic)
  }

  const customStyles = {
    multiValue: (provided: Record<string, unknown>, state: { data: AssigneeOption }) => ({
      ...provided,
      backgroundColor: state.data.isDynamic ? '#0dcaf0' : '#6c757d',
      color: '#ffffff',
      borderRadius: '0.375rem',
      padding: '0.25rem 0.5rem',
      margin: '0.125rem',
    }),
    multiValueLabel: (provided: Record<string, unknown>) => ({
      ...provided,
      color: '#ffffff',
      fontSize: '0.875rem',
    }),
    multiValueRemove: (provided: Record<string, unknown>) => ({
      ...provided,
      color: '#ffffff',
      ':hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
        color: '#ffffff',
      },
    }),
  }

  return (
    <div className="docdot-input-select" style={{ opacity: disabled ? 0.6 : 1 }}>
      <div>
        <Select<AssigneeOption, true, GroupBase<AssigneeOption>>
          isMulti
          options={options}
          value={selectedOptions}
          onChange={handleChange}
          placeholder={t('select_users')}
          className="react-select-container"
          classNamePrefix="react-select"
          isClearable={true}
          isDisabled={disabled}
          isLoading={loading}
          styles={customStyles}
          filterOption={(option, inputValue) => {
            return option.label.toLowerCase().includes(inputValue.toLowerCase())
          }}
          components={{
            MultiValue: ({ data, removeProps }) => {
              const bgColor = data.isDynamic ? '#0dcaf0' : '#343a40'
              return (
                <div
                  className="tag-wrap btn p-0 d-flex align-items-center justify-content-center"
                  style={{
                    backgroundColor: bgColor,
                    color: '#ffffff',
                    borderRadius: '0.375rem',
                    padding: '0.375rem 0.75rem',
                    margin: '0.125rem',
                  }}
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      if (removeProps.onClick) {
                        removeProps.onClick(e as any)
                      }
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="btn p-0 d-flex align-items-center"
                    style={{
                      color: '#ffffff',
                      padding: '0',
                      margin: '0 5px',
                      gap: '0.25rem',
                    }}
                    title={t('remove_user')}
                  >
                    <i className="bi bi-x" style={{ fontSize: '1.125rem', display: 'flex', alignItems: 'center' }} />
                    <span style={{ fontSize: '0.875rem', padding: '0.125rem 0' }}>{data.label}</span>
                  </button>
                </div>
              )
            },
            ClearIndicator: ({ innerProps }) => (
              <div {...innerProps} className="react-select__clear-indicator" title={t('clear_all')}>
                <i className="bi bi-x" style={{ fontSize: '1rem' }} />
              </div>
            ),
          }}
        />
      </div>
    </div>
  )
}
